'use client';
import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@/components/shared/typography/typography.component';
import { useTranslation, useWindowDimensions } from '@/hook';
import { EFinanceStateUpdateValue, EFinanceType, InterestRate } from '@/types';
import TabButton from './tab-button';
import { FormProvider, useController, useForm } from 'react-hook-form';

import { TabCarPrice } from './tabs/car-price';
import { numberWithCommas, PMT, removeCommas } from '@/utils';
import { FinancingResult } from './result';

import { TabMonthlyInstallments } from './tabs/monthly-installments';

const CarFinanceCalculator = ({
    interestRates,
    min,
    max,
    financeType,
    minDownpayment,
    maxDownpayment,
}: {
    interestRates: InterestRate[];
    min: number;
    max: number;
    financeType: EFinanceType;
    minDownpayment: number;
    maxDownpayment: number;
}) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const methods = useForm({
        criteriaMode: 'all',
        reValidateMode: 'onChange',
        mode: 'all',
    });
    const [isClient, setIsClient] = useState(false);
    const { control } = methods;
    const [type, setType] = useState<EFinanceStateUpdateValue>(
        EFinanceStateUpdateValue.monthlyInstallments
    );

    const [downPayment, setDownPayment] = useState(20);
    const [tenorEMI, setTenorEMI] = useState(60);
    const [tenorCarPrice, setTenorCarPrice] = useState(60);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { width } = useWindowDimensions();
    const isDesktop = width > 850;
    useEffect(() => {
        methods.reset({
            originalPrice: (max + min) / 2,
            dpAmount: (max + min) / 4,
        });
    }, [min, max]);
    useEffect(() => {
        setIsClient(true);
    }, []);

    const validateEmi = (e: number) => {
        if (removeCommas(e) < 1000)
            return t('FinanceCalculator.errors.emiLess', {
                num: numberWithCommas(1000),
            });
        else if (
            removeCommas(e) > PMT(0.2525, 12, (max * maxDownpayment) / 100)
        )
            return t('FinanceCalculator.errors.emiGreater', {
                num: numberWithCommas(
                    PMT(0.2525, 12, (max * maxDownpayment) / 100)
                ),
            });
        else return true;
    };
    const validateOriginalPrice = (e: number) => {
        if (removeCommas(e) < min)
            return t('FinanceCalculator.errors.carPriceLess', {
                num: numberWithCommas(min),
            });
        else if (removeCommas(e) > max)
            return t('FinanceCalculator.errors.carPriceGreater', {
                num: numberWithCommas(max),
            });
        else return true;
    };
    const validateDpAmount = (e: number) => {
        if (removeCommas(e) < (min * minDownpayment) / 100)
            return t('FinanceCalculator.errors.dpLess', {
                num: numberWithCommas((min * minDownpayment) / 100),
            });
        else if (removeCommas(e) > (max * maxDownpayment) / 100)
            return t('FinanceCalculator.errors.dpGreater', {
                num: numberWithCommas((max * maxDownpayment) / 100),
            });
        else return true;
    };

    useController({
        name: 'originalPrice',
        control: control,
        defaultValue: (max + min) / 2,
        rules: {
            required: {
                value: true,
                message: t('FinanceCalculator.errors.monthlyRequired'),
            },
            validate: validateOriginalPrice,
        },
    });
    useController({
        name: 'emi',
        control: control,
        defaultValue: 3000,
        rules: {
            required: {
                value: true,
                message: t('FinanceCalculator.errors.carPriceRequired'),
            },
            validate: validateEmi,
        },
    });
    useController({
        name: 'dpAmount',
        control: control,

        defaultValue: (max + min) / 4,
        rules: {
            required: {
                value: true,
                message: t('FinanceCalculator.errors.carPriceRequired'),
            },
            validate: validateDpAmount,
        },
    });
    const tabClicked = (value: EFinanceType) => {
        if (isClient && typeof window !== 'undefined') {
            const newUrl = new URL(
                `/${lang}/car-finance/${value}`,
                window.location.origin
            );
            window.history.replaceState({}, '', newUrl);
        }
    };
    useEffect(() => {
        if (type && financeType) {
            if (
                financeType === EFinanceType.CAR_PRICE &&
                type !== EFinanceStateUpdateValue.monthlyInstallments
            ) {
                setType(EFinanceStateUpdateValue.monthlyInstallments);
            }
            if (
                financeType === EFinanceType.EMI &&
                type !== EFinanceStateUpdateValue.carPrice
            ) {
                setType(EFinanceStateUpdateValue.carPrice);
            }
        }
    }, []);
    return (
        <FormProvider {...methods}>
            <div className={styles.container}>
                {/* {!interestRates || !isSuccess ? (
        <FinanceCalculatorLoadingSkeleton />
      ) : ( */}
                <div className={styles.innerContainer}>
                    <div className={styles.sectionColumn}>
                        <div className={styles.infoSection}>
                            <Typography
                                className={styles.infoTitle}
                                variant={'subtitle2Bold'}
                            >
                                {t('FinanceCalculator.title')}
                            </Typography>
                            <Typography
                                className={styles.infoSubtitle}
                                variant="body1"
                            >
                                {t('FinanceCalculator.subtitle')}
                            </Typography>
                        </div>
                        <div className={styles.horizontalTabs}>
                            <TabButton
                                active={type === 'monthlyInstallments'}
                                onClick={() => {
                                    setType(
                                        EFinanceStateUpdateValue.monthlyInstallments
                                    );
                                    tabClicked(EFinanceType.CAR_PRICE);
                                }}
                                //label swapped between tabs for SELLING-890
                                label={t('FinanceCalculator.carPrice')}
                            />
                            <TabButton
                                active={type === 'carPrice'}
                                onClick={() => {
                                    setType(EFinanceStateUpdateValue.carPrice);
                                    tabClicked(EFinanceType.EMI);
                                }}
                                //label swapped between tabs for SELLING-890
                                label={t(
                                    'FinanceCalculator.monthlyInstallment'
                                )}
                            />
                        </div>
                        {type === EFinanceStateUpdateValue.carPrice ? (
                            <TabCarPrice
                                max={max}
                                min={min}
                                interestRates={interestRates}
                                downPayment={downPayment}
                                setDownPayment={setDownPayment}
                                setTenorEMI={setTenorEMI}
                                tenorEMI={tenorEMI}
                                setTenorCarPrice={setTenorCarPrice}
                                tenorCarPrice={tenorCarPrice}
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                type={type}
                                minDownpayment={minDownpayment}
                                maxDownpayment={maxDownpayment}
                            />
                        ) : (
                            <TabMonthlyInstallments
                                max={max}
                                min={min}
                                interestRates={interestRates}
                                downPayment={downPayment}
                                setDownPayment={setDownPayment}
                                setTenorEMI={setTenorEMI}
                                tenorEMI={tenorEMI}
                                tenorCarPrice={tenorCarPrice}
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                type={type}
                                minDownpayment={minDownpayment}
                                maxDownpayment={maxDownpayment}
                            />
                        )}
                    </div>
                    {isDesktop && (
                        <div className={styles.sectionColumn}>
                            <FinancingResult
                                min={min}
                                max={max}
                                interestRates={interestRates}
                                downPayment={downPayment}
                                tenorEMI={tenorEMI}
                                tenorCarPrice={tenorCarPrice}
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                type={type}
                            />
                        </div>
                    )}
                </div>
                {/* )} */}
            </div>
        </FormProvider>
    );
};

export default CarFinanceCalculator;
